/**
 * Economic news calendar — docked drawer (same chrome as Notes / Journal /
 * Help), opened by the Calendar icon in the right rail.
 *
 * Current week only: the ForexFactory feed publishes exactly one week and has
 * no past/future variants, so there is nothing to page through.
 *
 * Source model — the two sources carry the SAME events (FTMO's calendar is
 * itself built on ForexFactory), so the source filter is single-select rather
 * than a multi-select: showing both at once would duplicate every row. What
 * FTMO adds is the "Restricted event" marking, which is why it's the default
 * and why the Restricted-only toggle is bound to it.
 *
 * Every time is UTC end-to-end (features/time_handling.md) — formatted through
 * `chartTime`'s helpers so a release reads the same wall clock as the candle
 * it lands on.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setNewsOpen } from "@/state/slices/uiSlice";
import {
  refreshNews,
  setNewsFilters,
  setNewsShowOnChart,
  type ImpactFloor,
  type NewsFilters,
} from "@/state/slices/newsSlice";
import type { NewsEvent, NewsImpact, NewsSourceId } from "@/api/types";
import { zonedParts } from "@/utils/chartTime";
import styles from "./NewsPanel.module.css";

/** A release is "live" within this window either side of its timestamp —
 *  also FTMO's restricted window, so the highlight doubles as the no-trade
 *  indicator. */
const LIVE_WINDOW_MS = 2 * 60 * 1000;
/** Anything sooner than this is flagged as imminent. */
const SOON_MS = 30 * 60 * 1000;

const IMPACT_ORDER: Record<NewsImpact, number> = {
  high: 3,
  medium: 2,
  low: 1,
  holiday: 0,
  none: 0,
};

const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const z2 = (n: number) => String(n).padStart(2, "0");

// ── helpers ──────────────────────────────────────────────────────────

const CalendarGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
  </svg>
);

const RefreshGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M20 12a8 8 0 1 1-2.3-5.6" strokeLinecap="round" />
    <path d="M20 4v4h-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" strokeLinecap="round" />
  </svg>
);

/** Chart-overlay toggle glyph — a candle with a marker pole beside it. */
const OnChartGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M7 4v16" strokeLinecap="round" strokeDasharray="3 3" />
    <path d="M7 4l5 2-5 2" strokeLinejoin="round" />
    <rect x="15" y="9" width="4" height="8" rx="1" />
    <path d="M17 6v3M17 17v3" strokeLinecap="round" />
  </svg>
);

/** "Mon 04 Aug" in UTC — the day-group heading. The heading is sticky, so
 *  its · UTC suffix stays on screen for every row under it. */
function fmtDayHeading(ms: number): string {
  const p = zonedParts(Math.floor(ms / 1000));
  return `${SHORT_DAYS[p.weekday]} ${z2(p.day)} ${SHORT_MONTHS[p.month]}`;
}

/** "12:30" in UTC. */
function fmtTime(ms: number): string {
  const p = zonedParts(Math.floor(ms / 1000));
  return `${z2(p.hour)}:${z2(p.minute)}`;
}

/** "2026-08-07 12:30 UTC" — the unambiguous full stamp, used as the row
 *  tooltip so hovering never leaves the zone in doubt. */
function fmtFullStamp(ms: number): string {
  const p = zonedParts(Math.floor(ms / 1000));
  return (
    `${p.year}-${z2(p.month + 1)}-${z2(p.day)} ` +
    `${z2(p.hour)}:${z2(p.minute)} UTC`
  );
}

/** Day key for grouping — UTC calendar day, matching the chart's buckets. */
function dayKey(ms: number): string {
  const p = zonedParts(Math.floor(ms / 1000));
  return `${p.year}-${z2(p.month + 1)}-${z2(p.day)}`;
}

type RowState = "released" | "live" | "soon" | "upcoming";

function rowState(eventMs: number, nowMs: number): RowState {
  const delta = eventMs - nowMs;
  if (Math.abs(delta) <= LIVE_WINDOW_MS) return "live";
  if (delta < 0) return "released";
  if (delta <= SOON_MS) return "soon";
  return "upcoming";
}

/** Impact as 1-3 filled dots; holiday/none render as a muted single dot. */
function ImpactDots({ impact }: { impact: NewsImpact }) {
  const filled = IMPACT_ORDER[impact] ?? 0;
  const cls =
    impact === "high"
      ? styles.dotHigh
      : impact === "medium"
        ? styles.dotMedium
        : impact === "low"
          ? styles.dotLow
          : styles.dotNone;
  return (
    <span className={styles.dots} title={impact}>
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={`${styles.dot} ${n <= filled ? cls : styles.dotEmpty}`}
        />
      ))}
    </span>
  );
}

// ── panel ────────────────────────────────────────────────────────────

export function NewsPanel() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.newsOpen);

  // Fetching + polling live in `useNewsFeed` (mounted in App) so the chart
  // overlay keeps working while this drawer is closed.
  const payload = useAppSelector((s) => s.news.payload);
  const loading = useAppSelector((s) => s.news.loading);
  const refreshing = useAppSelector((s) => s.news.refreshing);
  const error = useAppSelector((s) => s.news.error);
  const filters = useAppSelector((s) => s.news.filters);
  const showOnChart = useAppSelector((s) => s.news.showOnChart);
  // Drives the live/released row states without re-fetching.
  const [nowMs, setNowMs] = useState(() => Date.now());

  const patch = useCallback(
    (next: Partial<NewsFilters>) => {
      dispatch(setNewsFilters(next));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!open) return;
    const tick = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(tick);
  }, [open]);

  // Close on Escape, mirroring the Notes / Journal / Help drawers.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setNewsOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  const onRefresh = useCallback(() => {
    void dispatch(refreshNews());
  }, [dispatch]);

  const allEvents = payload?.events ?? [];

  // Currency chips come from the data, not a hardcoded list, so a new
  // currency appearing in the feed shows up without a code change.
  const currencyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of allEvents) {
      if (e.source === filters.source) set.add(e.currency);
    }
    return [...set].sort((a, b) =>
      a === "ALL" ? 1 : b === "ALL" ? -1 : a.localeCompare(b),
    );
  }, [allEvents, filters.source]);

  const visible = useMemo(() => {
    const floor = filters.impact === "all" ? -1 : IMPACT_ORDER[filters.impact];
    // The currency chips are a ForexFactory-only control (see below), so the
    // filter behind them is skipped on the FTMO source — otherwise a selection
    // made on one source would keep hiding rows on the other with no visible
    // chip to explain it.
    const wanted =
      filters.source === "ftmo" ? new Set<string>() : new Set(filters.currencies);
    return allEvents.filter((e) => {
      if (e.source !== filters.source) return false;
      if (filters.restrictedOnly && !e.restricted) return false;
      if (floor >= 0 && (IMPACT_ORDER[e.impact] ?? 0) < floor) return false;
      if (wanted.size > 0 && !wanted.has(e.currency)) return false;
      return true;
    });
  }, [allEvents, filters]);

  // Group into UTC days, preserving the server's chronological order.
  const days = useMemo(() => {
    const out: { key: string; label: string; events: NewsEvent[] }[] = [];
    for (const e of visible) {
      const ms = Date.parse(e.time_utc);
      const key = dayKey(ms);
      const last = out[out.length - 1];
      if (last && last.key === key) last.events.push(e);
      else out.push({ key, label: fmtDayHeading(ms), events: [e] });
    }
    return out;
  }, [visible]);

  const sourceMeta = useMemo(
    () => payload?.sources.find((s) => s.source === filters.source) ?? null,
    [payload, filters.source],
  );

  const restrictedCount = useMemo(
    () =>
      allEvents.filter((e) => e.source === filters.source && e.restricted)
        .length,
    [allEvents, filters.source],
  );

  if (!open) return null;

  const isFtmo = filters.source === "ftmo";

  return (
    <aside className={styles.drawer} aria-label="Economic news calendar">
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.titleIco}>
            <CalendarGlyph />
          </span>
          News
          <span className={styles.tzBadge} title="All times are UTC">
            UTC
          </span>
        </div>
        <div className={styles.headerActions}>
          {/* Chart overlay toggle — high-impact events as dashed vertical
              lines. Lives here rather than in the toolbar because this is
              where the user is already thinking about events. */}
          <button
            type="button"
            className={`${styles.iconBtn} ${showOnChart ? styles.iconBtnOn : ""}`}
            title={
              showOnChart
                ? "Hide high-impact events on the chart"
                : "Show high-impact events on the chart"
            }
            aria-label="Toggle events on chart"
            aria-pressed={showOnChart}
            onClick={() => dispatch(setNewsShowOnChart(!showOnChart))}
          >
            <OnChartGlyph />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Refresh from source"
            aria-label="Refresh news"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <span className={refreshing ? styles.spin : undefined}>
              <RefreshGlyph />
            </span>
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Close"
            aria-label="Close news panel"
            onClick={() => dispatch(setNewsOpen(false))}
          >
            ×
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        {/* Source — single-select: both sources carry the same events. */}
        <div className={styles.segRow}>
          <div className={styles.seg} role="group" aria-label="News source">
            {(
              [
                ["ftmo", "FTMO"],
                ["forexfactory", "Forex Factory"],
              ] as [NewsSourceId, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`${styles.segBtn} ${
                  filters.source === id ? styles.segBtnActive : ""
                }`}
                aria-pressed={filters.source === id}
                onClick={() =>
                  patch({
                    source: id,
                    // The chips are per-source, so a selection made on one
                    // must not survive into the other. Restricted-only is
                    // re-derived by the reducer.
                    currencies: [],
                  })
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.segRow}>
          <div className={styles.seg} role="group" aria-label="Minimum impact">
            {(
              [
                ["high", "High"],
                ["medium", "Med+"],
                ["low", "Low+"],
                ["all", "All"],
              ] as [ImpactFloor, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`${styles.segBtn} ${
                  filters.impact === id ? styles.segBtnActive : ""
                }`}
                aria-pressed={filters.impact === id}
                onClick={() => patch({ impact: id })}
              >
                {label}
              </button>
            ))}
          </div>
          {isFtmo && (
            <button
              type="button"
              className={`${styles.restrictBtn} ${
                filters.restrictedOnly ? styles.restrictBtnActive : ""
              }`}
              aria-pressed={filters.restrictedOnly}
              title={
                `FTMO restricted events only — no opening or closing a ` +
                `position within 2 min either side (Standard accounts)`
              }
              onClick={() => patch({ restrictedOnly: !filters.restrictedOnly })}
            >
              <LockGlyph />
              Restricted
              <span className={styles.restrictCount}>{restrictedCount}</span>
            </button>
          )}
        </div>

        {/* Currency chips are ForexFactory-only. On the FTMO source they
            filter the BUCKET while the rows display FTMO's instrument label,
            so a "USD" chip hiding a row that reads "Crude Oil" reads as a bug.
            Restricted-only is the filter that source actually needs. */}
        {!isFtmo && currencyOptions.length > 0 && (
          <div className={styles.chips}>
            <button
              type="button"
              className={`${styles.chip} ${
                filters.currencies.length === 0 ? styles.chipActive : ""
              }`}
              onClick={() => patch({ currencies: [] })}
            >
              All
            </button>
            {currencyOptions.map((c) => {
              const active = filters.currencies.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                  aria-pressed={active}
                  onClick={() =>
                    patch({
                      currencies: active
                        ? filters.currencies.filter((x) => x !== c)
                        : [...filters.currencies, c],
                    })
                  }
                >
                  {c}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.body}>
        {error && <div className={styles.error}>{error}</div>}
        {loading && !payload && <div className={styles.empty}>Loading…</div>}
        {!loading && payload && days.length === 0 && (
          <div className={styles.empty}>
            No events match these filters this week.
          </div>
        )}

        {days.map((day) => (
          <div key={day.key} className={styles.dayGroup}>
            <div className={styles.dayHeading}>
              {day.label}
              <span className={styles.dayTz}>UTC</span>
            </div>
            {day.events.map((e) => {
              const ms = Date.parse(e.time_utc);
              const state = rowState(ms, nowMs);
              return (
                <div
                  key={e.id}
                  className={`${styles.row} ${styles[`row_${state}`]} ${
                    isFtmo ? styles.rowInstrumentCol : ""
                  } ${e.restricted ? styles.rowRestricted : ""}`}
                >
                  <div className={styles.rowTime} title={fmtFullStamp(ms)}>
                    {state === "live" && (
                      <span className={styles.livePulse} aria-hidden />
                    )}
                    {fmtTime(ms)}
                    <span className={styles.rowTz}>UTC</span>
                  </div>
                  {/* FTMO's own `instrument` label where it published one —
                      that is the scope the restriction applies to. Falls back
                      to the currency bucket for ForexFactory rows and for days
                      FTMO has not published. */}
                  <div
                    className={styles.rowCcy}
                    title={
                      e.instrument
                        ? "Instruments FTMO applies this event to, as " +
                          "published by FTMO"
                        : undefined
                    }
                  >
                    {e.instrument || e.currency}
                  </div>
                  <div className={styles.rowMain}>
                    <div className={styles.rowTitle}>
                      {e.restricted && (
                        <span
                          className={styles.lockBadge}
                          title="FTMO restricted event — no open/close within 2 min either side"
                        >
                          <LockGlyph />
                        </span>
                      )}
                      {e.title}
                    </div>
                    {(e.actual || e.forecast || e.previous) && (
                      <div className={styles.rowVals}>
                        <span>
                          A <b>{e.actual || "—"}</b>
                        </span>
                        <span>
                          F <b>{e.forecast || "—"}</b>
                        </span>
                        <span>
                          P <b>{e.previous || "—"}</b>
                        </span>
                      </div>
                    )}
                  </div>
                  <ImpactDots impact={e.impact} />
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className={styles.foot}>
        <span>
          {sourceMeta?.fetched_at
            ? `Updated ${fmtTime(Date.parse(sourceMeta.fetched_at))} UTC`
            : "Not yet fetched"}
          {" · "}
          {visible.length} event{visible.length === 1 ? "" : "s"} this week
        </span>
        {isFtmo && sourceMeta?.mode === "hybrid" && (
          <span
            className={styles.derivedBadge}
            title={
              sourceMeta.note ||
              "Part of this week carries FTMO's own restricted marking; the " +
                "rest uses the local ruleset because FTMO publishes no data " +
                "beyond the current day."
            }
          >
            partial
          </span>
        )}
        {isFtmo && sourceMeta?.mode === "derived" && (
          <span
            className={styles.derivedBadge}
            title={
              sourceMeta.note ||
              "FTMO's calendar was unreachable — events come from Forex Factory " +
                "and restricted flags from the local ruleset."
            }
          >
            derived
          </span>
        )}
        {sourceMeta && !sourceMeta.ok && (
          <span className={styles.staleBadge} title={sourceMeta.note}>
            stale
          </span>
        )}
      </div>
    </aside>
  );
}
